import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, combineLatest, debounceTime } from 'rxjs';
import { formatDate } from 'shared/util/dates.utils';
import { waitForTranslationLoaded } from 'shared/util/wait-translations-loaded';

/**
 * @description
 * This component is used to display translated form errors
 * It takes the error object created by the control's validator and will attempt to translate it using the 'generalKeywords.errors.form.' translation key as a base (by default)
 * Some errors require additional interpolation parameters, which are used to substitute keys in the translated string surrounded by {{}}
 *
 * @param parentForm
 * The parent form of the control
 *
 * @param controlPath
 * The control name for which the error should be shown
 *
 * @param controlLabel
 * (Optional) Specify a custom label for the control (used in some errors where the field name is specified)
 *
 * @param errorMessageOverrides
 * (Optional) Provides a way to override certain error messages or interpolation parameters in the final error string.
 * Has two keys, both optional: errorKey, and interpolationParams
 * for example:
 *      - errorKey (string value): Will override the specified key with a custom error message
 *      - interpolationParams (object value): Will override the specified keys in the interpolationParams object with a custom value (must be translated BEFORE being passed to this component)
 *
 * @example
 * <app-form-error [parentForm]="form" [controlPath]="'toDate'" [controlLabel]="'reports.parameters.newReport.toDate'" />
 * (The "required" validation error message contains a "fieldName" key in the translated string, which is substituted by the [controlLabel] parameter)
 *
 * @example
 * <app-form-error [parentForm]="form" [controlPath]="'toDate'" [errorMessageOverrides]="{
 *                  lowerThanDate: {
 *                      interpolationParams: {
 *                          comparedAgainstField: 'reports.parameters.newReport.toDate' | translate
 *                      }
 *                  },
 *              }"/>
 * (The "lowerThanDate" validation message will have the "comparedAgainstField" interpolation parameter substituted with the specified string, in this case it translates the field name)
 */
@Component({
    selector: 'app-form-error',
    templateUrl: './form-error.component.html',
    styleUrls: ['./form-error.component.scss'],
    imports: [TranslateModule],
})
export class FormErrorComponent implements OnInit {
    @Input() displaySingleError: boolean = false;
    @Input() showError!: boolean;
    @Input() hideError = false;
    @Input() dismissible: boolean = true;
    @Input() controlLabel: string = '';
    @Input() errorMessageOverrides: {
        [key: string]: {
            errorKey?: string;
            interpolationParams?: { [key: string]: string };
        };
    } = {};

    @Input() set controlName(value: string) {
        this._controlName = value;
    }

    get controlName(): string {
        return this._controlName;
    }

    @Input() set parentForm(value: AbstractControl) {
        this._parentForm = value;
        this.setControl();
    }

    get parentForm(): AbstractControl {
        return this._parentForm;
    }

    @Input() set controlPath(value: string) {
        this._controlPath = value;
        this.setControl();
    }

    get controlPath(): string {
        return this._controlPath;
    }

    public control!: AbstractControl | null;
    public errorMessages!: { errorKey: string; interpolationParams: unknown }[];

    private _parentForm!: AbstractControl;
    private _controlPath!: string;
    private _controlName: string = '';

    constructor(
        private destroyRef: DestroyRef,
        private translateService: TranslateService,
    ) {}

    public ngOnInit(): void {
        // We want to get the error message when it inits so that we can display
        // it if the control is marked as touched programmatically without user interaction.
        waitForTranslationLoaded(this.translateService).subscribe(() => this.getErrorMessage());

        if (!this.control) {
            if (!environment.production) {
                console.warn('No control in error component.', this);
            }
            return;
        }

        combineLatest([this.control?.statusChanges, this.control?.valueChanges] as Observable<any>[])
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(10))
            .subscribe(() => this.getErrorMessage());
    }

    private setControl() {
        this.control = this._controlPath ? this._parentForm.get(this._controlPath) : this._parentForm;
    }

    //TODO: All messages should come from translations
    private getErrorMessage() {
        const fieldPaths = this._controlPath?.split('/') || EMPTY;
        let fieldName = fieldPaths[fieldPaths?.length - 1];
        fieldName = this._controlName ? this._controlName : fieldName?.split('.').join(' ').splitByCapital().toSentenceCase();

        const errorKeys = this.control?.errors ? Object.keys(this.control.errors) : [];
        this.errorMessages = [];

        if (this.control && this.control.errors) {
            for (let i = 0; i < errorKeys.length; i++) {
                const errorKey = errorKeys[i];
                let finalFieldName: string;
                let errorMessage = `generalKeywords.errors.form.${errorKey}`;
                let interpolationParams: { [key: string]: string } = {};

                if (this.controlLabel?.trim() && this.translateService.instant(this.controlLabel.trim())) {
                    finalFieldName = this.translateService.instant(this.controlLabel.trim());
                } else {
                    finalFieldName = fieldName;
                }

                interpolationParams['fieldName'] = finalFieldName;

                switch (errorKey) {
                    case 'minLength':
                    case 'minlength':
                        interpolationParams['requiredLength'] = this.control.errors[errorKey].requiredLength;
                        errorMessage = 'generalKeywords.errors.form.minLength';
                        break;
                    case 'maxLength':
                    case 'maxlength':
                        interpolationParams['requiredLength'] = this.control.errors[errorKey].requiredLength;
                        errorMessage = 'generalKeywords.errors.form.maxLength';
                        break;
                    case 'email':
                        break;
                    case 'mustMatch':
                        if (Array.isArray(this.control.errors[errorKey])) {
                            interpolationParams['firstControl'] = this.control.errors[errorKey][0];
                            interpolationParams['secondControl'] = this.control.errors[errorKey][1];
                        } else {
                            interpolationParams['firstControl'] = fieldName;
                            interpolationParams['secondControl'] = this.control.errors[errorKey].controlToMatch
                                .splitByCapital()
                                .toLowerCase();
                        }
                        break;
                    case 'date':
                        errorMessage = 'dynamicForm.validators.date';
                        break;
                    case 'dataType':
                        errorMessage = this.control.errors[errorKey];
                        break;
                    case 'passwordPattern':
                        errorMessage = ''; // We'll build the error message and push it to the array here
                        this.errorMessages.push({
                            errorKey: 'generalKeywords.errors.form.passwordPattern',
                            interpolationParams: { failedChecks: this.control.errors[errorKey].numberOfFailedChecks - 1 },
                        });

                        if (!this.control.errors[errorKey].hasNumber) {
                            this.errorMessages.push({
                                errorKey: 'generalKeywords.errors.form.passwordPatternHasNumber',
                                interpolationParams: {},
                            });
                        }
                        if (!this.control.errors[errorKey].hasSymbol) {
                            this.errorMessages.push({
                                errorKey: 'generalKeywords.errors.form.passwordPatternHasSymbol',
                                interpolationParams: {},
                            });
                        }
                        if (!this.control.errors[errorKey].hasUpper) {
                            this.errorMessages.push({
                                errorKey: 'generalKeywords.errors.form.passwordPatternHasUpper',
                                interpolationParams: {},
                            });
                        }
                        if (!this.control.errors[errorKey].hasLower) {
                            this.errorMessages.push({
                                errorKey: 'generalKeywords.errors.form.passwordPatternHasLower',
                                interpolationParams: {},
                            });
                        }
                        break;
                    case 'pattern':
                        if (this.control.errors[errorKey].requiredPattern.includes('\\-')) {
                            errorMessage = 'generalKeywords.errors.form.patternWithDashes';
                        }
                        break;
                    case 'requiredAsGroup':
                        errorMessage = 'generalKeywords.errors.form.required';
                        break;
                    case 'number': {
                        const errorMessages: string[] = [];
                        const errors = this.control.errors[errorKey];
                        const messages: { [key: string]: string } = {
                            type: 'dynamicForm.validators.number',
                            min: 'dynamicForm.validators.min',
                            max: 'dynamicForm.validators.max',
                            step: 'dynamicForm.validators.step',
                            noScientific: 'dynamicForm.validators.noScientific',
                        };

                        // Concatenate all the error keys that are related to the number type
                        ['type', 'min', 'max', 'step', 'noScientific'].forEach((key) => {
                            if (errors[key]) {
                                errorMessages.push(this.translateService.instant(messages[key], { [key]: errors[key][key] }));
                            }
                        });
                        errorMessage = errorMessages.join(' ');
                        break;
                    }
                    case 'mutuallyExclusiveRequired':
                    case 'mutuallyExclusiveRequiredArray':
                        interpolationParams['requiredFields'] = this.control?.errors[errorKey]
                            .join(',')
                            .replaceAll(',', ', ')
                            .splitByCapital()
                            .toTitleCase();
                        break;
                    case 'listingsRequireListingsPermissionsError':
                        errorMessage = 'users.supplierUsers.edit.permissions.listingsRequireListingsPermissionsError';
                        break;
                    case 'atLeastOnePermissionError':
                        errorMessage = 'users.supplierUsers.edit.permissions.atLeastOnePermissionError';
                        break;
                    case 'notAllowedPermissions':
                        errorMessage = 'users.supplierUsers.edit.permissions.notAllowedPermissions';
                        interpolationParams['permissions'] = this.control?.errors[errorKey].permissions;
                        break;
                    case 'min':
                        errorMessage = 'dynamicForm.validators.min';
                        interpolationParams.min = this.control?.errors[errorKey].min;
                        break;
                    case 'max':
                        errorMessage = 'dynamicForm.validators.max';
                        interpolationParams.max = this.control?.errors[errorKey].max;
                        break;
                    case 'lowerThan':
                    case 'greaterThan': {
                        let comparedAgainstControlLabel;
                        let translatedControlLabel;

                        if (this.controlLabel !== String.EMPTY) {
                            comparedAgainstControlLabel = this.controlLabel.replace(
                                this.controlPath,
                                this.control?.errors[errorKey].comparedAgainstField,
                            );
                            translatedControlLabel = this.translateService.instant(comparedAgainstControlLabel);
                        }

                        errorMessage = 'dynamicForm.validators.' + errorKey;
                        interpolationParams['fieldName'] = translatedControlLabel
                            ? translatedControlLabel
                            : this.control?.errors[errorKey].comparedAgainstField;
                        interpolationParams['value'] = this.control?.errors[errorKey]['expectedValue' + errorKey.capitalizeFirstLetter()];
                        break;
                    }
                    case 'maximumSelections':
                        interpolationParams['maximumSelections'] = this.control.errors[errorKey];
                        errorMessage = 'generalKeywords.errors.form.maximumSelections';
                        break;
                    case 'minDate': {
                        const equalAllowed = this.control?.errors[errorKey].equalAllowed;
                        const isTodayPreset = this.control?.errors[errorKey].minDateIsToday;
                        errorMessage = 'generalKeywords.errors.form.' + errorKey;
                        errorMessage += equalAllowed ? '.equalAllowed' : '';
                        errorMessage += isTodayPreset ? '.today' : '.customValue';

                        if (!isTodayPreset) {
                            interpolationParams['minValue'] =
                                formatDate(this.control?.errors[errorKey].minValue, 'dd MMMM yyyy') ??
                                this.control?.errors[errorKey].minValue;
                        }
                        break;
                    }
                    case 'maxDate': {
                        const equalAllowed = this.control?.errors[errorKey].equalAllowed;
                        errorMessage = 'generalKeywords.errors.form.' + errorKey;
                        errorMessage += equalAllowed ? '.inclusive' : '.exclusive';

                        interpolationParams['maxValue'] =
                            formatDate(this.control?.errors[errorKey].maxValue, 'dd MMMM yyyy') ?? this.control?.errors[errorKey].maxValue;
                        break;
                    }
                    case 'distinctDates':
                        errorMessage = 'generalKeywords.errors.form.' + errorKey;
                        interpolationParams['fieldName'] = this.translateService.instant(this.control?.errors[errorKey]?.fieldName ?? '');
                        interpolationParams['otherFieldName'] = this.translateService.instant(
                            this.control?.errors[errorKey]?.otherFieldName ?? '',
                        );
                        break;
                    case 'dateRangeLimit':
                        errorMessage = 'generalKeywords.errors.form.' + errorKey;
                        if (this.control?.errors[errorKey].minDays) {
                            errorMessage += '.minDaysAllowed';
                            interpolationParams['minDaysInRange'] = this.control?.errors[errorKey].minDays;
                        } else {
                            errorMessage += '.maxDaysAllowed';
                            interpolationParams['maxDaysInRange'] = this.control?.errors[errorKey].maxDays;
                        }
                        break;
                    case 'dateRangeInput':
                        errorMessage = 'generalKeywords.errors.form.' + errorKey;
                        interpolationParams['dateDelimiter'] = this.control?.errors[errorKey].delimiter;
                        break;
                    // Below are all the error messages that don't require special handling
                    case 'noScientific':
                    case 'listingDetailsRequired':
                    case 'listingProfileRequired':
                    case 'checkboxGroupAtLeastOneSelectionReq':
                    case 'emailListPattern':
                    case 'required':
                    case 'spendingLimitRequired':
                    case 'listingAlreadyInImportList':
                    case 'mappedIdAlreadyInImportList':
                    case 'channelMappedIdPatternMismatch':
                    case 'dateRangeNotSequential':
                        break;
                    case 'minimumSubinputSelections':
                        errorMessage = 'generalKeywords.errors.form.minimumSubinputSelections';
                        interpolationParams['expectedMinimumSelections'] = this.control.errors[errorKey].expectedMinimumSelections;
                        break;
                    default:
                        errorMessage = 'generalKeywords.errors.form.default';
                }

                if (errorMessage) {
                    if (this.errorMessageOverrides[errorKey]) {
                        if (this.errorMessageOverrides[errorKey].errorKey) {
                            errorMessage = this.errorMessageOverrides[errorKey].errorKey
                                ? this.errorMessageOverrides[errorKey].errorKey!
                                : errorMessage;
                        }
                        if (this.errorMessageOverrides[errorKey].interpolationParams) {
                            interpolationParams = { ...interpolationParams, ...this.errorMessageOverrides[errorKey].interpolationParams! };
                        }
                    }

                    this.errorMessages.push({ errorKey: errorMessage, interpolationParams });
                }

                if (this.displaySingleError) {
                    break;
                }
            }
        }
    }

    dismissMessage() {
        if (!this.dismissible) {
            return;
        }
        this.control?.markAsUntouched();
    }
}
